import { memo, useCallback, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "components/DialogV2";
import DialogHeader from "components/DialogV2/DialogHeader";
import DialogTitle from "components/DialogV2/DialogTitle";
import DialogClose from "components/DialogV2/DialogClose";
import DialogContent from "components/DialogV2/DialogContent";
import DialogActions from "components/DialogV2/DialogActions";
import Button from "components/Button";
import Typography from "components/Typography";
import VisuallyHidden from "components/VisuallyHidden";
import { Status } from "types/Status";

interface DeleteOneRepMaxDialogProps {
  open: boolean;
  /** Describes the entry being deleted, e.g. "150 kg on Aug 10, 2023". */
  entry?: string;
  onCancel: () => void;
  onConfirm: () => void;
  /** Called once the dialog has finished closing, after it has restored focus. */
  onExited?: () => void;
  status?: Status;
}

function DeleteOneRepMaxDialog(props: DeleteOneRepMaxDialogProps) {
  const { entry, onConfirm, onCancel, onExited, open, status } = props;
  const { t } = useTranslation();
  const deleting = status === "fetching";
  const entryId = useId();

  // Closing mid-request would let the pending delete close whichever dialog is open when it resolves.
  const handleOnClose = useCallback(() => {
    if (deleting) return;
    onCancel();
  }, [deleting, onCancel]);

  const memoSlotProps = useMemo(() => ({ transition: { onExited } }), [onExited]);

  const handleOnSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      onConfirm();
    },
    [onConfirm]
  );

  return (
    <Dialog open={open} onClose={handleOnClose} width={400} slotProps={memoSlotProps} aria-describedby={entryId}>
      <form onSubmit={handleOnSubmit}>
        <DialogHeader>
          <DialogTitle>{t("charting.one_rep_max_delete_dialog_title")}</DialogTitle>
          <DialogClose disabled={deleting} />
        </DialogHeader>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 0.5 }}
        >
          <Typography textAlign="center" fontWeight="fontWeightMedium" data-sensitive>
            {t("charting.one_rep_max_delete_dialog_message")}
          </Typography>
          <VisuallyHidden id={entryId}>{entry}</VisuallyHidden>
          <Typography variant="TextS" textAlign="center">
            {t("charting.one_rep_max_delete_dialog_description")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 2, pt: 2 }}>
          <Button type="submit" variant="danger" size="small" fullWidth disabled={deleting} loading={deleting}>
            {t("global.delete_button_text")}
          </Button>
          <Button variant="tertiary" size="small" fullWidth disabled={deleting} onClick={handleOnClose}>
            {t("global.cancel_button_text")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default memo(DeleteOneRepMaxDialog);
